import { useEffect } from 'react';
import { ArrowRight, Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHero, SocialShare } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { getAllBlogPosts } from '@lib/blog';

export default function BlogPage() {
  const posts = getAllBlogPosts();
  const [searchParams, setSearchParams] = useSearchParams();

  const postsPerPage = 9;
  const totalPages = Math.max(1, Math.ceil(posts.length / postsPerPage));

  const rawPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPage = Number.isNaN(rawPage) ? 1 : Math.min(Math.max(1, rawPage), totalPages);

  const startIndex = (currentPage - 1) * postsPerPage;
  const paginatedPosts = posts.slice(startIndex, startIndex + postsPerPage);

  // Scroll to blog section when page changes
  useEffect(() => {
    const el = document.getElementById('blog-grid');
    if (el && rawPage !== 1) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage, rawPage]);

  // ItemList structured data for SEO - only current page items
  const blogListStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: paginatedPosts.map((post, index) => ({
      '@type': 'ListItem',
      position: startIndex + index + 1,
      item: {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        datePublished: `${post.date}T00:00:00+01:00`,
        url: `https://thecisonetwork.com/news/${post.slug}`,
        author: {
          '@type': 'Organization',
          name: 'The CISO Network',
        },
      },
    })),
  };

  const goToPage = (page: number) => {
    if (page === 1) {
      setSearchParams({});
    } else {
      setSearchParams({ page: String(page) });
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogListStructuredData) }}
      />
      <PageHero
        title="Insights from Security Leaders"
        eyebrow="News"
        seoTitle="Fractional CISO Cybersecurity Leadership Insights | The CISO Network"
        seoDescription="Insights, guides, and thought leadership from our network of experienced CISOs."
      />

      {/* Intro */}
      <section className="pt-12 pb-12 bg-white">
        <div className="container-custom max-w-3xl text-center">
          <p className="text-muted-foreground leading-relaxed">
            Stay across the issues that matter to security leaders. Practical guidance, threat
            analysis, and strategic thinking drawn across finance, healthcare, SaaS, and beyond from
            our network of experienced fractional CISOs.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section id="blog-grid" className="pb-12 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {paginatedPosts.map(post => (
              <Link
                key={post.slug}
                to={`/news/${post.slug}`}
                className="group card-custom relative flex flex-col"
              >
                {post.image && (
                  <div className="-mx-8 -mt-8 mb-6 overflow-hidden rounded-t-xl">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full aspect-[16/9] object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex flex-col flex-grow items-start">
                  <span className="inline-flex w-fit px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-medium mb-4">
                    {post.category}
                  </span>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-3 group-hover:text-secondary transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-grow">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(post.date).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <div className="mt-auto pt-4 flex items-center justify-between w-full text-sm text-secondary font-medium border-t border-border/50">
                    <span className="flex items-center">
                      Read article{' '}
                      <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </span>
                    <SocialShare
                      slug={post.slug}
                      title={post.title}
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav
              aria-label="Blog pagination"
              className="flex items-center justify-center gap-2 mt-12"
            >
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center justify-center w-6 h-6 rounded border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-3 w-3" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  aria-current={page === currentPage ? 'page' : undefined}
                  className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-medium transition-colors ${
                    page === currentPage
                      ? 'bg-secondary text-white'
                      : 'border border-border text-foreground hover:bg-muted'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center justify-center w-6 h-6 rounded border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="h-3 w-3" />
              </button>
            </nav>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Looking for Security Insights for Your Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Our fractional CISOs can help you implement the strategies and frameworks discussed in
            our articles. Book a call to discuss your security needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
